import React, { useState } from 'react';
import {
  AreaInput,
  Button,
  Calendar,
  Grid,
  ICalendarValue,
  ISpruceFieldProps,
  SelectField,
  SpaceWrapper,
  SpruceField,
} from 'ui-toolkit';
import styled from 'styled-components';
import { Form, Formik } from 'formik';
import { DateTime } from 'luxon';
import * as yup from 'yup';
import { optionsFromDisplay } from '../../../../../../ui-toolkit/enums/optionsFromDisplay';

const StyledModalGrid = styled(Grid)`
  justify-items: center;
`;

interface IEditClosingDateInlineProps {
  value: string | null;
  onUpdateOrder: (value: string | null, reasonType: string | null, customReason: string | null) => void;
  onClose: () => void;
  highlightDate?: 'today' | string;
}

export const DelayTypes = {
  SPRUCE_DELAY: 'Spruce Delay',
  SELLER_DELAY: 'Seller Delay',
  BUYER_DELAY: 'Buyer Delay',
  LENDER_DELAY: 'Lender Delay',
  CLIENT_DELAY: 'Client Delay',
  OTHER: 'Other',
};

const closingDateSchema = yup
  .string()
  .nullable()
  .test(
    'closing_date',
    'Enter a valid date',
    (value: string | null | undefined) => value === null || value === undefined || DateTime.fromISO(value).isValid
  );

const pastValidationSchema = () =>
  yup.object({
    closing_date: closingDateSchema,
  });

const futureValidationSchema = () =>
  yup.object({
    closing_date: closingDateSchema,
    reasonType: yup.string().nullable().required(),
    customReason: yup.string().nullable().when('reasonType', { is: 'OTHER', then: yup.string().nullable().required() }),
  });

export const EditClosingDateInline: React.FC<IEditClosingDateInlineProps> = ({
  value,
  onUpdateOrder,
  onClose,
  highlightDate,
}) => {
  const [isNewDateInFuture, setIsNewDateInFuture] = useState(false);

  const onValueChange = (newValue: ICalendarValue) => {
    if (!newValue.value || !value) {
      setIsNewDateInFuture(false);
    } else {
      setIsNewDateInFuture(DateTime.fromISO(newValue.value).toJSDate() > DateTime.fromISO(value).toJSDate());
    }
  };

  return (
    <SpaceWrapper>
      <Formik
        initialValues={{
          value,
          reasonType: null,
          customReason: null,
        }}
        validationSchema={isNewDateInFuture ? futureValidationSchema() : pastValidationSchema()}
        enableReinitialize={true}
        onSubmit={({ value, reasonType, customReason }) => {
          onUpdateOrder(value, reasonType, customReason);
          onClose();
        }}
        onReset={() => {
          onUpdateOrder(null, null, null);
          onClose();
        }}
      >
        {({ handleSubmit, values, setFieldValue }) => (
          <Form onSubmit={handleSubmit}>
            <StyledModalGrid>
              <Calendar
                inputDate={values.value}
                inCard={false}
                onChange={(value) => {
                  onValueChange(value);
                  setFieldValue('value', value.value);
                }}
                highlightDate={highlightDate}
              />
              <Button intent={'ghost'} type={'reset'}>
                Clear Date
              </Button>
              {isNewDateInFuture && (
                <div style={{ width: 200 }}>
                  <SelectField
                    name="reasonType"
                    data-test-id={'reasonType'}
                    options={optionsFromDisplay(DelayTypes)}
                    label={`Reason`}
                    isClearable
                    placeholder={`Select a Reason`}
                    isRequired
                  />
                </div>
              )}
              {isNewDateInFuture && values.reasonType === 'OTHER' && (
                <div style={{ width: 200 }}>
                  <SpruceField name={'customReason'}>
                    {({ field }: ISpruceFieldProps) => (
                      <AreaInput
                        {...field}
                        rows={1}
                        onBlur={() => field.onChange(field.value ? field.value.trim() : field.value)}
                        label={'Custom reason'}
                        placeholder={'Reason for Delay'}
                        isRequired
                      />
                    )}
                  </SpruceField>
                </div>
              )}
              <Button intent={'primary'} type={'submit'} data-test-id={'closing-date-update-button'}>
                Update Order
              </Button>
            </StyledModalGrid>
          </Form>
        )}
      </Formik>
    </SpaceWrapper>
  );
};
